import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, CardSubtitle, CardTitle, Field, Input, Select, useToast } from '@keobook/ui';
import { Layout } from '../components/Layout';
import { useMoneyFormatter } from '../lib/useCurrency';
import { api, type Customer, type Invoice, type InvoiceItem } from '../lib/api';
import { US_STATES } from '../lib/us-states';

/**
 * Invoicing — single page, three tabs: Invoices, Customers, Items.
 * PR-1 ships CRUD only. PR-2 adds "Save & send" + PDF + hosted page;
 * PR-3 wires the journal-entry posting on payment.
 */
type Tab = 'customers' | 'invoices' | 'items' | 'deposits' | 'recurring';

export function Invoicing() {
  const [params, setParams] = useSearchParams();
  // George 2026-06-24: Customers should be the default landing tab.
  // Most workflows start with "pick a customer" rather than "find an invoice".
  const tab = (params.get('tab') as Tab) || 'customers';
  const setTab = (t: Tab) => setParams({ tab: t });

  return (
    <Layout>
      <div className="kb-page" style={{ maxWidth: 1120, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <h1
          style={{
            fontSize: 26,
            fontWeight: 600,
            color: 'var(--kb-gray-900)',
            margin: '0 0 0.5rem',
          }}
        >
          Invoicing
        </h1>
        <p style={{ fontSize: 13, color: 'var(--kb-gray-400)', margin: '0 0 1rem' }}>
          Create invoices for your customers. Draft invoices are editable; sending an invoice (PR-2)
          posts it to the books and emails the customer.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 6,
            marginBottom: '1rem',
            borderBottom: '1px solid var(--kb-gray-200)',
          }}
        >
          {(['customers', 'invoices', 'items', 'deposits', 'recurring'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              style={{
                background: 'none',
                border: 'none',
                padding: '8px 14px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                color: tab === t ? 'var(--kb-brand-green, #3CA960)' : 'var(--kb-gray-600)',
                borderBottom:
                  tab === t ? '2px solid var(--kb-brand-green, #3CA960)' : '2px solid transparent',
                marginBottom: -1,
                textTransform: 'capitalize',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'invoices' && <InvoicesTab />}
        {tab === 'customers' && <CustomersTab />}
        {tab === 'items' && <ItemsTab />}
        {tab === 'deposits' && <DepositsTab />}
        {tab === 'recurring' && <RecurringTab />}
      </div>
    </Layout>
  );
}

type Acct = { id: string; code: string; name: string; kind: string };

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------
function InvoicesTab() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [bankAccounts, setBankAccounts] = useState<Acct[]>([]);
  const [loading, setLoading] = useState(true);
  // ?new=<customerId> opens the form pre-populated for that customer.
  // Coming from the Customers tab via the "+ Invoice" row action.
  const newForCustomer = params.get('new') ?? '';
  const [creating, setCreating] = useState(Boolean(newForCustomer));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invs, custs, itms, accts] = await Promise.all([
        api.listInvoices(),
        api.listCustomers(),
        api.listItems(),
        api.getAccounts(),
      ]);
      setInvoices(invs.invoices);
      setCustomers(custs.customers);
      setItems(itms.items);
      // Deposit-to picker = bank-like asset accounts (Cash 1xxx, Undeposited 1150).
      // Skip A/R (1100) — depositing to A/R would defeat the point of marking paid.
      setBankAccounts(accts.accounts.filter((a) => a.kind === 'asset' && a.code !== '1100'));
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not load invoices.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <CardTitle>{invoices.length} invoices</CardTitle>
        <Button
          size="sm"
          onClick={() => setCreating(true)}
          disabled={customers.length === 0 || items.length === 0}
        >
          + New invoice
        </Button>
      </div>
      {customers.length === 0 && (
        <Card>
          <CardSubtitle>
            Add a <Link to="/invoicing?tab=customers">customer</Link> before creating an invoice.
          </CardSubtitle>
        </Card>
      )}
      {customers.length > 0 && items.length === 0 && (
        <Card>
          <CardSubtitle>
            Add at least one <Link to="/invoicing?tab=items">item</Link> (service / product) before
            creating an invoice.
          </CardSubtitle>
        </Card>
      )}
      {creating && (
        <NewInvoiceForm
          customers={customers}
          items={items}
          defaultCustomerId={newForCustomer || undefined}
          onClose={() => {
            setCreating(false);
            // Drop the ?new=… param so a refresh doesn't re-pop the form.
            if (newForCustomer) {
              params.delete('new');
              setParams(params, { replace: true });
            }
          }}
          onCreated={() => {
            setCreating(false);
            if (newForCustomer) {
              params.delete('new');
              setParams(params, { replace: true });
            }
            void load();
          }}
        />
      )}
      {loading ? (
        <Card>
          <CardSubtitle>Loading…</CardSubtitle>
        </Card>
      ) : invoices.length === 0 && !creating ? (
        <Card>
          <CardSubtitle>No invoices yet.</CardSubtitle>
        </Card>
      ) : (
        <table className="kb-report" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>#</th>
              <th style={{ textAlign: 'left' }}>Date</th>
              <th style={{ textAlign: 'left' }}>Customer</th>
              <th style={{ textAlign: 'left' }}>Due</th>
              <th style={{ textAlign: 'left' }}>Status</th>
              <th className="kb-report__amt">Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => {
              const cust = customers.find((c) => c.id === inv.customerId);
              return (
                <InvoiceRow
                  key={inv.id}
                  invoice={inv}
                  customerName={cust?.displayName ?? '—'}
                  customerHasEmail={Boolean(cust?.primaryEmail)}
                  bankAccounts={bankAccounts}
                  onChanged={() => void load()}
                />
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

function InvoiceRow({
  invoice,
  customerName,
  customerHasEmail,
  bankAccounts,
  onChanged,
}: {
  invoice: Invoice;
  customerName: string;
  customerHasEmail: boolean;
  bankAccounts: Acct[];
  onChanged: () => void;
}) {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  const [busy, setBusy] = useState(false);
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [showVoid, setShowVoid] = useState(false);
  const [showCredit, setShowCredit] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showLateFee, setShowLateFee] = useState(false);

  async function send() {
    setBusy(true);
    try {
      const { payUrl, emailSent } = await api.sendInvoice(invoice.id);
      toast({
        tone: 'success',
        message: emailSent
          ? `Invoice sent. Customer link: ${payUrl}`
          : `Invoice posted (email delivery failed — share link: ${payUrl})`,
      });
      onChanged();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not send invoice.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        <td>{invoice.docNumber}</td>
        <td>{invoice.txnDate}</td>
        <td>{customerName}</td>
        <td>{invoice.dueDate}</td>
        <td>
          {invoice.paid ? (
            <>
              <span className="kb-pill kb-pill--ok">paid</span>
              {invoice.refundedAmountCents > 0 && (
                <span
                  className="kb-pill kb-pill--warn"
                  style={{ marginLeft: 6, fontSize: 11 }}
                  title={`Refunded ${fmt(invoice.refundedAmountCents)}`}
                >
                  refunded
                </span>
              )}
            </>
          ) : (
            <span className="kb-pill kb-pill--neutral">{invoice.lifecycle}</span>
          )}
          {invoice.disputed && (
            <span
              className="kb-pill kb-pill--warn"
              style={{ marginLeft: 6, fontSize: 11, background: '#fcebeb', color: '#a32d2d' }}
              title="A Stripe dispute (chargeback) is open against a payment on this invoice. Funds are at risk."
            >
              disputed
            </span>
          )}
        </td>
        <td className="kb-report__amt">
          {fmt(invoice.totalCents)}
          {invoice.lateFeeAmountCents > 0 && (
            <div style={{ fontSize: 11, color: 'var(--kb-red-600, #a32d2d)' }}>
              + {fmt(invoice.lateFeeAmountCents)} late fee
            </div>
          )}
          {invoice.lifecycle === 'posted' &&
            !invoice.paid &&
            invoice.balanceCents !== invoice.totalCents + invoice.lateFeeAmountCents && (
              <div style={{ fontSize: 11, color: 'var(--kb-gray-400)' }}>
                {fmt(invoice.balanceCents)} due
              </div>
            )}
        </td>
        <td>
          {invoice.lifecycle === 'draft' && customerHasEmail && (
            <Button size="sm" variant="primary" disabled={busy} onClick={send}>
              {busy ? 'Sending…' : 'Send'}
            </Button>
          )}
          {invoice.lifecycle === 'draft' && !customerHasEmail && (
            <span className="kb-muted" style={{ fontSize: 12 }}>
              Add email to send
            </span>
          )}
          {invoice.lifecycle === 'posted' && !invoice.paid && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Button size="sm" variant="primary" onClick={() => setShowMarkPaid(true)}>
                Mark paid
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowEdit(true)}>
                Edit
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCredit(true)}>
                Issue credit
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowLateFee(true)}>
                Apply late fee
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowVoid(true)}>
                Void
              </Button>
            </div>
          )}
          {invoice.paid && invoice.refundedAmountCents < invoice.totalCents && (
            <Button size="sm" variant="ghost" onClick={() => setShowRefund(true)}>
              Refund
            </Button>
          )}
        </td>
      </tr>
      {showMarkPaid && (
        <MarkPaidModal
          invoice={invoice}
          bankAccounts={bankAccounts}
          onClose={() => setShowMarkPaid(false)}
          onDone={() => {
            setShowMarkPaid(false);
            onChanged();
          }}
        />
      )}
      {showVoid && (
        <VoidModal
          invoice={invoice}
          onClose={() => setShowVoid(false)}
          onDone={() => {
            setShowVoid(false);
            onChanged();
          }}
        />
      )}
      {showCredit && (
        <CreditMemoModal
          invoice={invoice}
          onClose={() => setShowCredit(false)}
          onDone={() => {
            setShowCredit(false);
            onChanged();
          }}
        />
      )}
      {showRefund && (
        <RefundModal
          invoice={invoice}
          onClose={() => setShowRefund(false)}
          onDone={() => {
            setShowRefund(false);
            onChanged();
          }}
        />
      )}
      {showEdit && (
        <EditInvoiceModal
          invoice={invoice}
          onClose={() => setShowEdit(false)}
          onDone={() => {
            setShowEdit(false);
            onChanged();
          }}
        />
      )}
      {showLateFee && (
        <LateFeeModal
          invoice={invoice}
          onClose={() => setShowLateFee(false)}
          onDone={() => {
            setShowLateFee(false);
            onChanged();
          }}
        />
      )}
    </>
  );
}

function LateFeeModal({
  invoice,
  onClose,
  onDone,
}: {
  invoice: Invoice;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  // Empty = use the tenant's dunning config to compute the fee.
  const [override, setOverride] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const amountCents = override ? Math.round((Number(override) || 0) * 100) : undefined;
      const r = await api.applyLateFee(invoice.id, {
        amountCents,
        reason: reason.trim() || undefined,
      });
      toast({
        tone: 'success',
        message: `Late fee ${fmt(r.amountCents)} applied to ${invoice.docNumber}.`,
      });
      onDone();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not apply late fee.',
      });
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Apply late fee to ${invoice.docNumber}`} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--kb-gray-400)' }}>
          Posts Dr 1100 Accounts Receivable / Cr 4930 Late Fee Income. The fee bumps the customer's
          open balance — they'll see it on the statement and the A/R aging report.
        </p>
        <Field label="Amount override ($) — leave blank to use your dunning config">
          <Input
            type="number"
            min="0.01"
            step="0.01"
            value={override}
            onChange={(e) => setOverride(e.currentTarget.value)}
            placeholder="e.g. 25.00"
          />
        </Field>
        <Field label="Reason">
          <Input
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder="Optional — e.g. 'Net-30 terms, 45 days overdue'"
          />
        </Field>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button type="submit" disabled={busy}>
            {busy ? 'Applying…' : 'Apply late fee'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function EditInvoiceModal({
  invoice,
  onClose,
  onDone,
}: {
  invoice: Invoice;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  const [dueDate, setDueDate] = useState(invoice.dueDate);
  const [customerMemo, setCustomerMemo] = useState(invoice.customerMemo ?? '');
  const [privateNote, setPrivateNote] = useState(invoice.privateNote ?? '');
  const [busy, setBusy] = useState(false);
  // Line editing — opt-in. Most edits are just memos / dates, so we
  // keep the line table collapsed by default to avoid an intimidating
  // tx wall. Opening it loads the current lines + the revenue accounts
  // the user can pick from.
  const [showLines, setShowLines] = useState(false);
  const [accounts, setAccounts] = useState<Acct[]>([]);
  const [editLines, setEditLines] = useState<
    Array<{ description: string; qty: number; unitPriceCents: number; incomeAccountId: string }>
  >([]);
  const [loadingLines, setLoadingLines] = useState(false);

  const linesChanged = showLines && editLines.length > 0;
  const linesSubtotal = editLines.reduce((s, l) => s + Math.round(l.qty * l.unitPriceCents), 0);

  async function openLineEditor() {
    setShowLines(true);
    if (editLines.length > 0) return;
    setLoadingLines(true);
    try {
      const [det, accts] = await Promise.all([api.getInvoice(invoice.id), api.getAccounts()]);
      setAccounts(
        accts.accounts.filter((a) => a.kind === 'revenue' || a.kind === 'cost_of_revenue'),
      );
      setEditLines(
        det.invoice.lines.map((l) => ({
          description: l.description,
          qty: l.qty,
          unitPriceCents: l.unitPriceCents,
          incomeAccountId: l.incomeAccountIdSnapshot,
        })),
      );
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not load lines.',
      });
      setShowLines(false);
    } finally {
      setLoadingLines(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.updateInvoice(invoice.id, {
        dueDate: dueDate !== invoice.dueDate ? dueDate : undefined,
        customerMemo:
          customerMemo !== (invoice.customerMemo ?? '') ? customerMemo || null : undefined,
        privateNote: privateNote !== (invoice.privateNote ?? '') ? privateNote || null : undefined,
        lines: linesChanged ? editLines : undefined,
      });
      toast({ tone: 'success', message: `Invoice ${invoice.docNumber} updated.` });
      onDone();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not update invoice.',
      });
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Edit ${invoice.docNumber}`} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--kb-gray-400)' }}>
          Posted invoices with no payments or credits can be edited. Changing lines reverses the
          original journal entry and posts a new one with the updated totals — the GL stays
          balanced.
        </p>
        <Field label="Due date">
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.currentTarget.value)} />
        </Field>
        <Field label="Memo to customer">
          <Input
            value={customerMemo}
            onChange={(e) => setCustomerMemo(e.currentTarget.value)}
            placeholder="Visible on the invoice PDF + hosted page"
          />
        </Field>
        <Field label="Private note (not shown to customer)">
          <Input value={privateNote} onChange={(e) => setPrivateNote(e.currentTarget.value)} />
        </Field>

        <div
          style={{
            borderTop: '1px solid var(--kb-gray-200)',
            margin: '12px 0',
            paddingTop: 10,
          }}
        >
          {!showLines ? (
            <Button type="button" size="sm" variant="ghost" onClick={openLineEditor}>
              Edit line items…
            </Button>
          ) : loadingLines ? (
            <p style={{ fontSize: 13, color: 'var(--kb-gray-400)' }}>Loading lines…</p>
          ) : (
            <>
              <p style={{ fontSize: 12, color: 'var(--kb-gray-400)', margin: '0 0 6px' }}>
                Edit each line below. Saving replaces every line.
              </p>
              <table className="kb-report">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Description</th>
                    <th style={{ width: 60 }}>Qty</th>
                    <th style={{ width: 100 }}>Unit ($)</th>
                    <th style={{ textAlign: 'left' }}>Account</th>
                    <th className="kb-report__amt">Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {editLines.map((l, idx) => (
                    <tr key={idx}>
                      <td>
                        <Input
                          value={l.description}
                          onChange={(e) =>
                            setEditLines((prev) => {
                              const next = [...prev];
                              next[idx] = { ...next[idx]!, description: e.currentTarget.value };
                              return next;
                            })
                          }
                          required
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={l.qty}
                          onChange={(e) =>
                            setEditLines((prev) => {
                              const next = [...prev];
                              next[idx] = {
                                ...next[idx]!,
                                qty: Number(e.currentTarget.value) || 0,
                              };
                              return next;
                            })
                          }
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={(l.unitPriceCents / 100).toFixed(2)}
                          onChange={(e) =>
                            setEditLines((prev) => {
                              const next = [...prev];
                              next[idx] = {
                                ...next[idx]!,
                                unitPriceCents: Math.round(
                                  (Number(e.currentTarget.value) || 0) * 100,
                                ),
                              };
                              return next;
                            })
                          }
                        />
                      </td>
                      <td>
                        <Select
                          value={l.incomeAccountId}
                          onChange={(e) =>
                            setEditLines((prev) => {
                              const next = [...prev];
                              next[idx] = {
                                ...next[idx]!,
                                incomeAccountId: e.currentTarget.value,
                              };
                              return next;
                            })
                          }
                          required
                        >
                          {accounts.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.code} {a.name}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td className="kb-report__amt">
                        {fmt(Math.round(l.qty * l.unitPriceCents))}
                      </td>
                      <td>
                        {editLines.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditLines((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            ×
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="kb-report__total">
                    <td colSpan={4}>Subtotal</td>
                    <td className="kb-report__amt">{fmt(linesSubtotal)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() =>
                  setEditLines((prev) => [
                    ...prev,
                    {
                      description: '',
                      qty: 1,
                      unitPriceCents: 0,
                      incomeAccountId: accounts[0]?.id ?? '',
                    },
                  ])
                }
              >
                + Add line
              </Button>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function RefundModal({
  invoice,
  onClose,
  onDone,
}: {
  invoice: Invoice;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  const [payments, setPayments] = useState<
    Array<{
      id: string;
      totalCents: number;
      refundedAmountCents: number;
      depositToAccountCode: string;
      depositToAccountName: string;
      isStripe: boolean;
      method: string;
    }>
  >([]);
  const [paymentId, setPaymentId] = useState('');
  const [amountCents, setAmountCents] = useState(0);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    void api
      .listInvoicePayments(invoice.id)
      .then((r) => {
        if (!alive) return;
        // Only payments with some remaining unrefunded amount are eligible.
        const eligible = r.payments.filter((p) => p.refundedAmountCents < p.totalCents);
        setPayments(eligible);
        const first = eligible[0];
        if (first) {
          setPaymentId(first.id);
          setAmountCents(first.totalCents - first.refundedAmountCents);
        }
      })
      .catch((err: unknown) =>
        toast({
          tone: 'error',
          message: err instanceof Error ? err.message : 'Could not load payments.',
        }),
      )
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [invoice.id, toast]);

  const selected = payments.find((p) => p.id === paymentId);
  const remaining = selected ? selected.totalCents - selected.refundedAmountCents : 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!paymentId || amountCents <= 0 || amountCents > remaining) return;
    setBusy(true);
    try {
      const r = await api.refundPayment(paymentId, {
        amountCents,
        reason: reason.trim() || undefined,
      });
      toast({
        tone: 'success',
        message: `Refund of ${fmt(r.amountCents)} recorded${
          r.stripeRefundId ? ` (Stripe ${r.stripeRefundId})` : ''
        }.`,
      });
      onDone();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not refund payment.',
      });
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Refund payment on ${invoice.docNumber}`} onClose={onClose}>
      {loading ? (
        <p>Loading payments…</p>
      ) : payments.length === 0 ? (
        <p style={{ margin: 0 }}>No refundable payments on this invoice.</p>
      ) : (
        <form onSubmit={submit} noValidate>
          <p style={{ margin: '0 0 12px', fontSize: 14 }}>
            Posts Dr Sales Returns / Cr {selected?.depositToAccountCode ?? '—'}{' '}
            {selected?.depositToAccountName ?? ''}.
            {selected?.isStripe && ' Calls Stripe refund API.'}
          </p>
          <Field label="Payment" required>
            <Select
              value={paymentId}
              onChange={(e) => {
                const newId = e.currentTarget.value;
                setPaymentId(newId);
                const p = payments.find((x) => x.id === newId);
                if (p) setAmountCents(p.totalCents - p.refundedAmountCents);
              }}
              required
            >
              {payments.map((p) => (
                <option key={p.id} value={p.id}>
                  {fmt(p.totalCents)} {p.method} ({p.depositToAccountCode}) —{' '}
                  {fmt(p.totalCents - p.refundedAmountCents)} refundable
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Refund amount ($)" required>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              max={(remaining / 100).toFixed(2)}
              value={(amountCents / 100).toFixed(2)}
              onChange={(e) =>
                setAmountCents(Math.round((Number(e.currentTarget.value) || 0) * 100))
              }
              required
            />
          </Field>
          <Field label="Reason">
            <Input
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              placeholder="e.g. customer request, duplicate charge, dispute"
            />
          </Field>
          <p style={{ fontSize: 12, color: 'var(--kb-gray-400)', margin: '8px 0 0' }}>
            Refundable: {fmt(remaining)}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button
              type="submit"
              variant="danger"
              disabled={busy || !paymentId || amountCents <= 0 || amountCents > remaining}
            >
              {busy ? 'Refunding…' : 'Refund'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

function CreditMemoModal({
  invoice,
  onClose,
  onDone,
}: {
  invoice: Invoice;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  // Default the credit amount to the remaining balance — the common case
  // for "this one's on us" goodwill credits. User can dial down for partials.
  const [amountCents, setAmountCents] = useState(invoice.balanceCents);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (amountCents <= 0 || amountCents > invoice.balanceCents) return;
    setBusy(true);
    try {
      const r = await api.createCreditMemo({
        customerId: invoice.customerId,
        invoiceId: invoice.id,
        totalCents: amountCents,
        reason: reason.trim() || undefined,
      });
      toast({
        tone: 'success',
        message: `Credit ${r.creditMemo.docNumber} issued for ${fmt(amountCents)}.`,
      });
      onDone();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not issue credit.',
      });
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Issue credit against ${invoice.docNumber}`} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <p style={{ margin: '0 0 12px', fontSize: 14 }}>
          Posts Dr Sales Returns / Cr A/R and reduces the open balance. The original invoice stays
          on the books — useful for goodwill, returns, or partial write-offs.
        </p>
        <Field label="Amount ($)" required>
          <Input
            type="number"
            min="0.01"
            step="0.01"
            max={(invoice.balanceCents / 100).toFixed(2)}
            value={(amountCents / 100).toFixed(2)}
            onChange={(e) => setAmountCents(Math.round((Number(e.currentTarget.value) || 0) * 100))}
            required
          />
        </Field>
        <Field label="Reason">
          <Input
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder="e.g. returned widget, goodwill discount, billing error"
          />
        </Field>
        <p style={{ fontSize: 12, color: 'var(--kb-gray-400)', margin: '8px 0 0' }}>
          Remaining balance: {fmt(invoice.balanceCents)}
          {amountCents === invoice.balanceCents && (
            <span style={{ display: 'block', marginTop: 4, color: 'var(--kb-brand-green-text)' }}>
              Full credit — invoice will flip to paid.
            </span>
          )}
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button
            type="submit"
            disabled={busy || amountCents <= 0 || amountCents > invoice.balanceCents}
          >
            {busy ? 'Issuing…' : 'Issue credit'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function MarkPaidModal({
  invoice,
  bankAccounts,
  onClose,
  onDone,
}: {
  invoice: Invoice;
  bankAccounts: Acct[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  const [depositToAccountId, setDepositToAccountId] = useState(
    bankAccounts.find((a) => a.code === '1000')?.id ?? bankAccounts[0]?.id ?? '',
  );
  const [method, setMethod] = useState<'cash' | 'check' | 'ach' | 'card' | 'wire' | 'other'>(
    'check',
  );
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!depositToAccountId) return;
    setBusy(true);
    try {
      await api.markInvoicePaid(invoice.id, {
        depositToAccountId,
        method,
        paidDate,
      });
      toast({ tone: 'success', message: `Invoice ${invoice.docNumber} marked paid.` });
      onDone();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not mark paid.',
      });
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Mark ${invoice.docNumber} paid`} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <Field label="Deposit to" required>
          <Select
            value={depositToAccountId}
            onChange={(e) => setDepositToAccountId(e.currentTarget.value)}
            required
          >
            {bankAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} {a.name}
              </option>
            ))}
          </Select>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Method">
            <Select
              value={method}
              onChange={(e) =>
                setMethod(
                  e.currentTarget.value as 'cash' | 'check' | 'ach' | 'card' | 'wire' | 'other',
                )
              }
            >
              <option value="check">Check</option>
              <option value="cash">Cash</option>
              <option value="ach">ACH</option>
              <option value="wire">Wire</option>
              <option value="card">Card (offline)</option>
              <option value="other">Other</option>
            </Select>
          </Field>
          <Field label="Date">
            <Input
              type="date"
              value={paidDate}
              onChange={(e) => setPaidDate(e.currentTarget.value)}
            />
          </Field>
        </div>
        <p style={{ fontSize: 12, color: 'var(--kb-gray-400)', margin: '8px 0 0' }}>
          Records a {fmt(invoice.totalCents)} payment and posts the matching journal entry
          (Dr deposit account / Cr Accounts Receivable).
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button type="submit" disabled={busy || !depositToAccountId}>
            {busy ? 'Recording…' : 'Mark paid'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function VoidModal({
  invoice,
  onClose,
  onDone,
}: {
  invoice: Invoice;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await api.voidInvoice(invoice.id, { reason: reason.trim() });
      toast({
        tone: 'success',
        message: `Invoice ${invoice.docNumber} voided. Reversing entry posted.`,
      });
      onDone();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not void.',
      });
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Void ${invoice.docNumber}`} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        <p style={{ margin: '0 0 12px', fontSize: 14 }}>
          This reverses the original journal entry (Dr Revenue / Cr A/R) and stamps the invoice as
          void. The original entry stays on the books for audit.
        </p>
        <Field label="Reason" required>
          <Input
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder="e.g. duplicate, wrong customer, replaced by INV-…"
            required
          />
        </Field>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button type="submit" variant="danger" disabled={busy || !reason.trim()}>
            {busy ? 'Voiding…' : 'Void invoice'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

/** Lightweight inline modal — same portal style as DrillDownPanel. */
function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <tr>
      <td colSpan={7} style={{ padding: 0 }}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '6rem 1.5rem 1.5rem',
          }}
          onClick={onClose}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 480,
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
              padding: '18px 20px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <strong style={{ fontSize: 16 }}>{title}</strong>
              <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close">
                ×
              </Button>
            </div>
            {children}
          </div>
        </div>
      </td>
    </tr>
  );
}

/** Net-terms picker — George 2026-06-26 asked for 0 / 15 / 30 quick picks.
 *  60/45 are kept since they're common for B2B; "Custom…" falls through
 *  to the number input so unusual values still work. */
const TERMS_OPTIONS = [
  { value: 0, label: 'Due on receipt' },
  { value: 15, label: 'Net 15' },
  { value: 30, label: 'Net 30' },
  { value: 45, label: 'Net 45' },
  { value: 60, label: 'Net 60' },
];

function NewInvoiceForm({
  customers,
  items,
  defaultCustomerId,
  onClose,
  onCreated,
}: {
  customers: Customer[];
  items: InvoiceItem[];
  /** Pre-select this customer (passed when George clicks "+ Invoice" on a
   *  customer row). Falls back to last-used or first available. */
  defaultCustomerId?: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  // Default precedence: explicit prop → most-recently-used (localStorage) →
  // first customer in the list. Persisting last-used keeps the "create
  // another invoice for the same customer" path frictionless.
  const lastUsed =
    typeof window === 'undefined'
      ? ''
      : (localStorage.getItem('kb:last-invoice-customer-id') ?? '');
  const initialCustomerId =
    (defaultCustomerId && customers.some((c) => c.id === defaultCustomerId)
      ? defaultCustomerId
      : '') ||
    (customers.some((c) => c.id === lastUsed) ? lastUsed : '') ||
    customers[0]?.id ||
    '';
  const [customerId, setCustomerId] = useState(initialCustomerId);
  const selectedCustomer = customers.find((c) => c.id === customerId);
  const [txnDate, setTxnDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Default to the customer's standard terms, fall back to Net 30.
  const [termsDays, setTermsDays] = useState<number>(selectedCustomer?.defaultTermsDays ?? 30);
  // When the customer changes, snap terms back to that customer's default
  // so the dueDate auto-updates in the read-out below.
  useEffect(() => {
    if (selectedCustomer) setTermsDays(selectedCustomer.defaultTermsDays);
  }, [selectedCustomer]);
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<
    Array<{ itemId: string; description: string; qty: string; unitPrice: string }>
  >([
    items[0]
      ? {
          itemId: items[0].id,
          description: items[0].description ?? items[0].name,
          qty: '1',
          unitPrice: (items[0].unitPriceCents / 100).toFixed(2),
        }
      : { itemId: '', description: '', qty: '1', unitPrice: '' },
  ]);
  const [busy, setBusy] = useState(false);

  // Qty / unit price are held as raw strings so manual entry isn't fought by
  // reformatting on every keystroke, and long numbers aren't clipped.
  const parseQty = (s: string) => Number(String(s).replace(/[,\s]/g, '')) || 0;
  const parseUnitCents = (s: string) =>
    Math.round((Number(String(s).replace(/[$,\s]/g, '')) || 0) * 100);
  const lineAmountCents = (l: { qty: string; unitPrice: string }) =>
    Math.round(parseQty(l.qty) * parseUnitCents(l.unitPrice));
  const subtotal = lines.reduce((s, l) => s + lineAmountCents(l), 0);
  // Read-only dueDate preview computed from txnDate + termsDays.
  const dueDatePreview = (() => {
    const t = new Date(txnDate);
    if (Number.isNaN(t.getTime())) return '';
    t.setUTCDate(t.getUTCDate() + termsDays);
    return t.toISOString().slice(0, 10);
  })();

  function pickItem(idx: number, itemId: string) {
    const it = items.find((i) => i.id === itemId);
    setLines((prev) => {
      const next = [...prev];
      const current = next[idx];
      if (!current) return prev; // defensive: idx must be in range
      // George 2026-06-27: don't overwrite description / unit price that
      // the user has already customized. Only fill from the item record
      // when the field is still empty / zero. This lets the CPA add two
      // "Services" lines, override the description on each, and switch
      // the unit price per-line — picking the item a second time no
      // longer wipes their edits.
      const filledDescription = current.description?.trim()
        ? current.description
        : (it?.description ?? it?.name ?? current.description);
      const hasUnit = current.unitPrice.trim() !== '' && parseUnitCents(current.unitPrice) > 0;
      const filledUnitPrice = hasUnit
        ? current.unitPrice
        : ((it?.unitPriceCents ?? 0) / 100).toFixed(2);
      next[idx] = {
        ...current,
        itemId,
        description: filledDescription,
        unitPrice: filledUnitPrice,
      };
      return next;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!customerId) return;
    setBusy(true);
    try {
      await api.createInvoice({
        customerId,
        txnDate,
        termsDays,
        customerMemo: memo || undefined,
        lines: lines.map((l) => ({
          itemId: l.itemId || undefined,
          description: l.description,
          qty: parseQty(l.qty),
          unitPriceCents: parseUnitCents(l.unitPrice),
        })),
      });
      try {
        localStorage.setItem('kb:last-invoice-customer-id', customerId);
      } catch {
        /* private mode / quota — non-fatal */
      }
      toast({ tone: 'success', message: 'Invoice drafted.' });
      onCreated();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not create invoice.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>New invoice</CardTitle>
      <form onSubmit={submit} noValidate>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 140px 140px',
            gap: 12,
          }}
        >
          <Field label="Customer" required>
            <Select
              value={customerId}
              onChange={(e) => setCustomerId(e.currentTarget.value)}
              required
            >
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date" required>
            <Input
              type="date"
              value={txnDate}
              onChange={(e) => setTxnDate(e.currentTarget.value)}
              required
            />
          </Field>
          <Field label="Terms">
            <Select
              value={String(termsDays)}
              onChange={(e) => setTermsDays(Number(e.currentTarget.value) || 0)}
            >
              {TERMS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              {/* Surface a non-standard customer default (e.g. Net 7) so it
                  doesn't silently flip to Net 30 when the form loads. */}
              {!TERMS_OPTIONS.some((o) => o.value === termsDays) && (
                <option value={String(termsDays)}>Net {termsDays}</option>
              )}
            </Select>
          </Field>
        </div>
        {selectedCustomer && (
          <p style={{ fontSize: 12, color: 'var(--kb-gray-400)', margin: '6px 0 0' }}>
            Bill to <strong>{selectedCustomer.displayName}</strong>
            {selectedCustomer.billAddr.city
              ? ` · ${selectedCustomer.billAddr.city}${
                  selectedCustomer.billAddr.state ? `, ${selectedCustomer.billAddr.state}` : ''
                }`
              : ''}
            {dueDatePreview ? ` · Due ${dueDatePreview}` : ''}
          </p>
        )}

        <p style={{ fontSize: 12, color: 'var(--kb-gray-400)', margin: '14px 0 4px' }}>
          Add a line per service. The description and unit price auto-fill from the item, but you
          can override either per line — type over the auto-filled text.
        </p>
        <table className="kb-report" style={{ marginTop: 6 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Item</th>
              <th style={{ textAlign: 'left' }}>Description</th>
              <th>Qty</th>
              <th>Unit ($)</th>
              <th className="kb-report__amt">Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => (
              <tr key={idx}>
                <td>
                  <Select value={l.itemId} onChange={(e) => pickItem(idx, e.currentTarget.value)}>
                    <option value="">— pick item —</option>
                    {items.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name}
                      </option>
                    ))}
                  </Select>
                </td>
                <td>
                  <Input
                    value={l.description}
                    placeholder="Override the description for this line"
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setLines((prev) => {
                        const next = [...prev];
                        const cur = next[idx];
                        if (!cur) return prev;
                        next[idx] = { ...cur, description: v };
                        return next;
                      });
                    }}
                    // Select-all on focus so picking an item, then clicking
                    // the description, lets George just start typing to
                    // replace the auto-filled text in one motion.
                    onFocus={(e) => e.currentTarget.select()}
                  />
                </td>
                <td style={{ width: 90 }}>
                  <Input
                    inputMode="decimal"
                    value={l.qty}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setLines((prev) => {
                        const next = [...prev];
                        const cur = next[idx];
                        if (!cur) return prev;
                        next[idx] = { ...cur, qty: v };
                        return next;
                      });
                    }}
                    onFocus={(e) => e.currentTarget.select()}
                    style={{ textAlign: 'right', width: '100%' }}
                  />
                </td>
                <td style={{ width: 150 }}>
                  <Input
                    inputMode="decimal"
                    value={l.unitPrice}
                    placeholder="0.00"
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setLines((prev) => {
                        const next = [...prev];
                        const cur = next[idx];
                        if (!cur) return prev;
                        next[idx] = { ...cur, unitPrice: v };
                        return next;
                      });
                    }}
                    onFocus={(e) => e.currentTarget.select()}
                    style={{ textAlign: 'right', width: '100%' }}
                  />
                </td>
                <td className="kb-report__amt">{fmt(lineAmountCents(l))}</td>
                <td>
                  {lines.length > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      type="button"
                      onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      ×
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            <tr className="kb-report__total">
              <td colSpan={4}>Subtotal</td>
              <td className="kb-report__amt">{fmt(subtotal)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() =>
            setLines((prev) => [
              ...prev,
              items[0]
                ? {
                    itemId: items[0].id,
                    description: items[0].description ?? items[0].name,
                    qty: '1',
                    unitPrice: (items[0].unitPriceCents / 100).toFixed(2),
                  }
                : { itemId: '', description: '', qty: '1', unitPrice: '' },
            ])
          }
          // If items haven't loaded the button stays available but the
          // new row has no itemId — saving without an item is rejected by
          // the API. Disable instead so George can't tap a footgun.
          disabled={items.length === 0}
        >
          + Add line
        </Button>

        <Field label="Memo to customer" style={{ marginTop: 12 }}>
          <Input
            value={memo}
            onChange={(e) => setMemo(e.currentTarget.value)}
            placeholder="Optional note printed on the invoice"
          />
        </Field>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button type="submit" disabled={busy || !customerId}>
            {busy ? 'Saving…' : 'Save draft'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------
function CustomersTab() {
  const toast = useToast();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listCustomers();
      setCustomers(r.customers);
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not load customers.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <CardTitle>{customers.length} customers</CardTitle>
        <Button size="sm" onClick={() => setCreating(true)}>
          + New customer
        </Button>
      </div>
      {creating && (
        <NewCustomerForm
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
      {loading ? (
        <Card>
          <CardSubtitle>Loading…</CardSubtitle>
        </Card>
      ) : customers.length === 0 && !creating ? (
        <Card>
          <CardSubtitle>No customers yet.</CardSubtitle>
        </Card>
      ) : (
        <table className="kb-report" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Name</th>
              <th style={{ textAlign: 'left' }}>Email</th>
              <th style={{ textAlign: 'left' }}>Terms</th>
              <th style={{ textAlign: 'left' }}>City</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <CustomerRow key={c.id} customer={c} />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function CustomerRow({ customer }: { customer: Customer }) {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  const [, setParams] = useSearchParams();
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  async function previewStatement() {
    // Fetch the PDF as a blob (auth header attached) and open the local
    // blob URL — a bare window.open of the API URL would send no auth
    // header and 401 (George 2026-06-24).
    setPreviewing(true);
    try {
      const url = await api.customerStatementPdfBlobUrl(customer.id);
      window.open(url, '_blank');
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not load statement.',
      });
    } finally {
      setPreviewing(false);
    }
  }

  async function sendStatement() {
    if (!customer.primaryEmail) return;
    setSending(true);
    try {
      const r = await api.sendCustomerStatement(customer.id);
      toast({
        tone: 'success',
        message: `Statement sent — ${r.invoiceCount} open invoice${
          r.invoiceCount === 1 ? '' : 's'
        }, ${fmt(r.totalBalanceCents)} due.`,
      });
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not send statement.',
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <tr>
      <td>{customer.displayName}</td>
      <td>{customer.primaryEmail ?? '—'}</td>
      <td>Net {customer.defaultTermsDays}</td>
      <td>
        {customer.billAddr.city ?? '—'}
        {customer.billAddr.state ? `, ${customer.billAddr.state}` : ''}
      </td>
      <td>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button
            size="sm"
            variant="primary"
            // Jump to the Invoices tab with the new-invoice form open
            // pre-populated for THIS customer (George 2026-06-26).
            onClick={() => setParams({ tab: 'invoices', new: customer.id }, { replace: false })}
          >
            + Invoice
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={previewing}
            onClick={previewStatement}
            title="Preview the statement PDF in a new tab"
          >
            {previewing ? 'Loading…' : 'Preview'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={!customer.primaryEmail || sending}
            onClick={sendStatement}
            title={
              customer.primaryEmail ? 'Email the statement to the customer' : 'No email on file'
            }
          >
            {sending ? 'Sending…' : 'Send statement'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function NewCustomerForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [displayName, setDisplayName] = useState('');
  const [primaryEmail, setEmail] = useState('');
  const [defaultTermsDays, setTerms] = useState(30);
  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!displayName.trim()) return;
    setBusy(true);
    try {
      await api.createCustomer({
        displayName: displayName.trim(),
        primaryEmail: primaryEmail || undefined,
        defaultTermsDays,
        billAddr:
          line1 || city ? { line1, line2: null, city, state, zip, country: null } : undefined,
      });
      toast({ tone: 'success', message: 'Customer added.' });
      onCreated();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not create customer.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>New customer</CardTitle>
      <form onSubmit={submit} noValidate>
        <Field label="Display name" required>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            required
          />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 12 }}>
          <Field label="Email">
            <Input
              type="email"
              value={primaryEmail}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
          </Field>
          <Field label="Terms (days)">
            <Input
              type="number"
              min={0}
              max={365}
              value={defaultTermsDays}
              onChange={(e) => setTerms(Number(e.currentTarget.value) || 30)}
            />
          </Field>
        </div>
        <Field label="Address line">
          <Input value={line1} onChange={(e) => setLine1(e.currentTarget.value)} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px', gap: 12 }}>
          <Field label="City">
            <Input value={city} onChange={(e) => setCity(e.currentTarget.value)} />
          </Field>
          <Field label="State">
            <Select value={state} onChange={(e) => setState(e.currentTarget.value)}>
              <option value="">—</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="ZIP">
            <Input value={zip} onChange={(e) => setZip(e.currentTarget.value)} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button type="submit" disabled={busy || !displayName.trim()}>
            {busy ? 'Saving…' : 'Save customer'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------
function ItemsTab() {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [accounts, setAccounts] = useState<Acct[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<InvoiceItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [it, ac] = await Promise.all([api.listItems(), api.getAccounts()]);
      setItems(it.items);
      // Only revenue + cost-of-revenue accounts make sense for an item.
      setAccounts(ac.accounts.filter((a) => a.kind === 'revenue' || a.kind === 'cost_of_revenue'));
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not load items.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <CardTitle>{items.length} items</CardTitle>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setCreating(true);
          }}
          disabled={accounts.length === 0}
        >
          + New item
        </Button>
      </div>
      {(creating || editing) && (
        <ItemForm
          accounts={accounts}
          item={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void load();
          }}
        />
      )}
      {loading ? (
        <Card>
          <CardSubtitle>Loading…</CardSubtitle>
        </Card>
      ) : items.length === 0 && !creating ? (
        <Card>
          <CardSubtitle>No items yet. Add a service or product.</CardSubtitle>
        </Card>
      ) : (
        <table className="kb-report" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Name</th>
              <th style={{ textAlign: 'left' }}>Type</th>
              <th className="kb-report__amt">Unit price</th>
              <th style={{ textAlign: 'left' }}>Income account</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const acct = accounts.find((a) => a.id === it.incomeAccountId);
              return (
                <tr key={it.id}>
                  <td>{it.name}</td>
                  <td>{it.type}</td>
                  <td className="kb-report__amt">{fmt(it.unitPriceCents)}</td>
                  <td>{acct ? `${acct.code} ${acct.name}` : it.incomeAccountId.slice(0, 8)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setCreating(false);
                        setEditing(it);
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

function ItemForm({
  accounts,
  item,
  onClose,
  onSaved,
}: {
  accounts: Acct[];
  /** Present → edit an existing item; absent → create a new one. */
  item?: InvoiceItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = !!item;
  const toast = useToast();
  const [name, setName] = useState(item?.name ?? '');
  const [type, setType] = useState<'service' | 'noninventory'>(
    (item?.type as 'service' | 'noninventory') ?? 'service',
  );
  const [unitPrice, setUnitPrice] = useState(
    item ? (item.unitPriceCents / 100).toFixed(2) : '',
  );
  const [incomeAccountId, setAcctId] = useState(item?.incomeAccountId ?? accounts[0]?.id ?? '');
  const [busy, setBusy] = useState(false);

  const unitPriceCents = Math.round(
    (Number(String(unitPrice).replace(/[$,\s]/g, '')) || 0) * 100,
  );

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !incomeAccountId) return;
    setBusy(true);
    try {
      const body = { name: name.trim(), type, unitPriceCents, incomeAccountId };
      if (editing) {
        await api.updateItem(item!.id, body);
        toast({ tone: 'success', message: 'Item updated.' });
      } else {
        await api.createItem(body);
        toast({ tone: 'success', message: 'Item added.' });
      }
      onSaved();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : `Could not ${editing ? 'update' : 'create'} item.`,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>{editing ? 'Edit item' : 'New item'}</CardTitle>
      <form onSubmit={submit} noValidate>
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.currentTarget.value)} required />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
          <Field label="Type">
            <Select
              value={type}
              onChange={(e) => setType(e.currentTarget.value as 'service' | 'noninventory')}
            >
              <option value="service">Service</option>
              <option value="noninventory">Non-inventory</option>
            </Select>
          </Field>
          <Field label="Income account">
            <Select
              value={incomeAccountId}
              onChange={(e) => setAcctId(e.currentTarget.value)}
              required
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Default unit price ($)">
          <Input
            inputMode="decimal"
            value={unitPrice}
            placeholder="0.00"
            onChange={(e) => setUnitPrice(e.currentTarget.value)}
            onFocus={(e) => e.currentTarget.select()}
          />
        </Field>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button type="submit" disabled={busy || !name.trim() || !incomeAccountId}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Save item'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Deposits — sweep Undeposited Funds → bank account
// ---------------------------------------------------------------------------
type UndepositedPayment = {
  id: string;
  txnDate: string;
  totalCents: number;
  method: string;
  stripePaymentIntentId: string | null;
  customerId: string;
  customerName: string;
  invoiceId: string | null;
  invoiceDocNumber: string | null;
};

type PriorDeposit = {
  id: string;
  bankAccountId: string;
  txnDate: string;
  totalCents: number;
  notes: string | null;
  journalEntryId: string | null;
  lineCount: number;
  createdAt: string;
};

function DepositsTab() {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  const [pending, setPending] = useState<UndepositedPayment[]>([]);
  const [bankAccounts, setBankAccounts] = useState<Acct[]>([]);
  const [history, setHistory] = useState<PriorDeposit[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bankAccountId, setBankAccountId] = useState('');
  const [txnDate, setTxnDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [und, accts, hist] = await Promise.all([
        api.listUndepositedPayments(),
        api.getAccounts(),
        api.listDeposits(),
      ]);
      setPending(und.payments);
      // Bank-like accounts only: kind = asset, but exclude A/R + Undeposited
      // (you can't deposit Undeposited Funds into Undeposited Funds).
      setBankAccounts(
        accts.accounts.filter((a) => a.kind === 'asset' && a.code !== '1100' && a.code !== '1150'),
      );
      setHistory(hist.deposits);
      // Default to Cash — Operating if seeded.
      const cash = accts.accounts.find((a) => a.code === '1000');
      if (cash) setBankAccountId(cash.id);
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not load deposits.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedTotal = pending.filter((p) => selected[p.id]).reduce((s, p) => s + p.totalCents, 0);
  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);

  function toggleAll() {
    if (selectedIds.length === pending.length) {
      setSelected({});
    } else {
      setSelected(Object.fromEntries(pending.map((p) => [p.id, true])));
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (selectedIds.length === 0 || !bankAccountId) return;
    setSubmitting(true);
    try {
      const r = await api.createDeposit({
        bankAccountId,
        paymentIds: selectedIds,
        txnDate,
        notes: notes.trim() || undefined,
      });
      toast({
        tone: 'success',
        message: `Deposit of ${fmt(r.deposit.totalCents)} recorded (${r.deposit.lineCount} payment${r.deposit.lineCount === 1 ? '' : 's'}).`,
      });
      setSelected({});
      setNotes('');
      void load();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not record deposit.',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <CardTitle>Undeposited Funds → Bank Deposit</CardTitle>
      <CardSubtitle>
        Payments collected via Stripe (or manually marked into Undeposited Funds) sit here until you
        sweep them into your real bank account. Pick the payments that match the deposit on your
        bank statement.
      </CardSubtitle>

      {loading ? (
        <Card>
          <CardSubtitle>Loading…</CardSubtitle>
        </Card>
      ) : pending.length === 0 ? (
        <Card>
          <CardSubtitle>Nothing in Undeposited Funds right now.</CardSubtitle>
        </Card>
      ) : (
        <form onSubmit={submit} noValidate>
          <table className="kb-report" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === pending.length}
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
                <th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Customer</th>
                <th style={{ textAlign: 'left' }}>Invoice</th>
                <th style={{ textAlign: 'left' }}>Method</th>
                <th className="kb-report__amt">Amount</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <tr key={p.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!selected[p.id]}
                      onChange={(e) =>
                        setSelected((prev) => ({ ...prev, [p.id]: e.currentTarget.checked }))
                      }
                      aria-label={`Select payment ${p.id}`}
                    />
                  </td>
                  <td>{p.txnDate}</td>
                  <td>{p.customerName}</td>
                  <td>{p.invoiceDocNumber ?? '—'}</td>
                  <td>{p.method}</td>
                  <td className="kb-report__amt">{fmt(p.totalCents)}</td>
                </tr>
              ))}
              <tr className="kb-report__total">
                <td colSpan={5}>Selected total</td>
                <td className="kb-report__amt">{fmt(selectedTotal)}</td>
              </tr>
            </tbody>
          </table>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 180px',
              gap: 12,
              marginTop: 16,
            }}
          >
            <Field label="Deposit to" required>
              <Select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.currentTarget.value)}
                required
              >
                <option value="">— pick bank account —</option>
                {bankAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} {a.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Deposit date">
              <Input
                type="date"
                value={txnDate}
                onChange={(e) => setTxnDate(e.currentTarget.value)}
              />
            </Field>
          </div>
          <Field label="Notes">
            <Input
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              placeholder="Optional — e.g. 'Stripe payout reference 06/12'"
            />
          </Field>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button
              type="submit"
              disabled={submitting || selectedIds.length === 0 || !bankAccountId}
            >
              {submitting ? 'Recording…' : `Record deposit (${fmt(selectedTotal)})`}
            </Button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--kb-gray-400)', marginTop: 8 }}>
            Posts Dr {bankAccounts.find((a) => a.id === bankAccountId)?.code ?? '—'} / Cr 1150
            Undeposited Funds and locks each selected payment to this deposit so it can't be
            re-included.
          </p>
        </form>
      )}

      {history.length > 0 && (
        <>
          <CardTitle style={{ marginTop: 24 }}>Recent deposits</CardTitle>
          <table className="kb-report" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Account</th>
                <th>Payments</th>
                <th className="kb-report__amt">Total</th>
                <th style={{ textAlign: 'left' }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {history.map((d) => {
                const acct = bankAccounts.find((a) => a.id === d.bankAccountId);
                return (
                  <tr key={d.id}>
                    <td>{d.txnDate}</td>
                    <td>{acct ? `${acct.code} ${acct.name}` : d.bankAccountId.slice(0, 8)}</td>
                    <td style={{ textAlign: 'center' }}>{d.lineCount}</td>
                    <td className="kb-report__amt">{fmt(d.totalCents)}</td>
                    <td>{d.notes ?? ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Recurring invoices
// ---------------------------------------------------------------------------
type RecurringTemplate = Awaited<ReturnType<typeof api.listRecurringInvoices>>['templates'][number];

function RecurringTab() {
  const toast = useToast();
  const [templates, setTemplates] = useState<RecurringTemplate[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [accounts, setAccounts] = useState<Acct[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tpls, custs, accts] = await Promise.all([
        api.listRecurringInvoices(),
        api.listCustomers(),
        api.getAccounts(),
      ]);
      setTemplates(tpls.templates);
      setCustomers(custs.customers);
      setAccounts(
        accts.accounts.filter((a) => a.kind === 'revenue' || a.kind === 'cost_of_revenue'),
      );
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not load recurring invoices.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runNow(t: RecurringTemplate) {
    try {
      const r = await api.runRecurringInvoice(t.id);
      toast({
        tone: 'success',
        message: `New draft invoice created. Next run: ${r.nextRunDate}${
          r.deactivated ? ' (template now inactive — past end date)' : ''
        }.`,
      });
      void load();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not run template.',
      });
    }
  }

  async function toggleActive(t: RecurringTemplate) {
    try {
      await api.updateRecurringInvoice(t.id, { active: !t.active });
      void load();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not update template.',
      });
    }
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <CardTitle>{templates.length} recurring templates</CardTitle>
        <Button
          size="sm"
          onClick={() => setCreating(true)}
          disabled={customers.length === 0 || accounts.length === 0}
        >
          + New recurring template
        </Button>
      </div>
      {creating && (
        <NewRecurringForm
          customers={customers}
          accounts={accounts}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
      {loading ? (
        <Card>
          <CardSubtitle>Loading…</CardSubtitle>
        </Card>
      ) : templates.length === 0 && !creating ? (
        <Card>
          <CardSubtitle>
            No recurring invoices yet. Use this for monthly retainers, quarterly fees, or any
            invoice that goes out on a schedule.
          </CardSubtitle>
        </Card>
      ) : (
        <table className="kb-report" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Template</th>
              <th style={{ textAlign: 'left' }}>Customer</th>
              <th style={{ textAlign: 'left' }}>Cadence</th>
              <th style={{ textAlign: 'left' }}>Next run</th>
              <th style={{ textAlign: 'left' }}>Runs</th>
              <th style={{ textAlign: 'left' }}>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => {
              const cust = customers.find((c) => c.id === t.customerId);
              return (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td>{cust?.displayName ?? '—'}</td>
                  <td>
                    every {t.frequencyInterval} {t.frequencyUnit}
                    {t.frequencyInterval === 1 ? '' : 's'}
                  </td>
                  <td>{t.nextRunDate}</td>
                  <td style={{ textAlign: 'center' }}>{t.runCount}</td>
                  <td>
                    {t.active ? (
                      <span className="kb-pill kb-pill--ok">active</span>
                    ) : (
                      <span className="kb-pill kb-pill--neutral">paused</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={!t.active}
                        onClick={() => runNow(t)}
                      >
                        Run now
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(t)}>
                        {t.active ? 'Pause' : 'Resume'}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

function NewRecurringForm({
  customers,
  accounts,
  onClose,
  onCreated,
}: {
  customers: Customer[];
  accounts: Acct[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const toast = useToast();
  const fmt = useMoneyFormatter();
  const [name, setName] = useState('Monthly retainer');
  const [customerId, setCustomerId] = useState(customers[0]?.id ?? '');
  const [frequencyUnit, setFrequencyUnit] = useState<'day' | 'week' | 'month'>('month');
  const [frequencyInterval, setFrequencyInterval] = useState(1);
  const [nextRunDate, setNextRunDate] = useState(() => {
    const d = new Date();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState('');
  const [lines, setLines] = useState<
    Array<{ description: string; qty: number; unitPriceCents: number; incomeAccountId: string }>
  >([
    {
      description: 'Monthly bookkeeping',
      qty: 1,
      unitPriceCents: 50000,
      incomeAccountId: accounts[0]?.id ?? '',
    },
  ]);
  const [busy, setBusy] = useState(false);

  const subtotal = lines.reduce((s, l) => s + Math.round(l.qty * l.unitPriceCents), 0);

  function updateLine(idx: number, patch: Partial<(typeof lines)[number]>) {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx]!, ...patch };
      return next;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!customerId || lines.some((l) => !l.incomeAccountId)) return;
    setBusy(true);
    try {
      await api.createRecurringInvoice({
        customerId,
        name,
        frequencyUnit,
        frequencyInterval,
        nextRunDate,
        endDate: endDate || undefined,
        lines,
      });
      toast({ tone: 'success', message: 'Recurring template created.' });
      onCreated();
    } catch (err) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not create template.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardTitle>New recurring template</CardTitle>
      <form onSubmit={submit} noValidate>
        <Field label="Template name" required>
          <Input value={name} onChange={(e) => setName(e.currentTarget.value)} required />
        </Field>
        <Field label="Customer" required>
          <Select
            value={customerId}
            onChange={(e) => setCustomerId(e.currentTarget.value)}
            required
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </Select>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="Every">
            <Input
              type="number"
              min={1}
              value={frequencyInterval}
              onChange={(e) => setFrequencyInterval(Number(e.currentTarget.value) || 1)}
            />
          </Field>
          <Field label="Unit">
            <Select
              value={frequencyUnit}
              onChange={(e) => setFrequencyUnit(e.currentTarget.value as 'day' | 'week' | 'month')}
            >
              <option value="day">day(s)</option>
              <option value="week">week(s)</option>
              <option value="month">month(s)</option>
            </Select>
          </Field>
          <Field label="Next run">
            <Input
              type="date"
              value={nextRunDate}
              onChange={(e) => setNextRunDate(e.currentTarget.value)}
              required
            />
          </Field>
        </div>
        <Field label="End date (optional — leave blank to run forever)">
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.currentTarget.value)} />
        </Field>
        <hr
          style={{ border: 'none', borderTop: '1px solid var(--kb-gray-200)', margin: '14px 0' }}
        />
        <p style={{ fontSize: 12, color: 'var(--kb-gray-400)', margin: '0 0 8px' }}>
          Lines materialize onto every generated invoice. Add one row per service or product.
        </p>
        <table className="kb-report">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Description</th>
              <th style={{ width: 70 }}>Qty</th>
              <th style={{ width: 110 }}>Unit ($)</th>
              <th style={{ textAlign: 'left' }}>Income account</th>
              <th className="kb-report__amt">Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => (
              <tr key={idx}>
                <td>
                  <Input
                    value={l.description}
                    onChange={(e) => updateLine(idx, { description: e.currentTarget.value })}
                    required
                  />
                </td>
                <td>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={l.qty}
                    onChange={(e) => updateLine(idx, { qty: Number(e.currentTarget.value) || 0 })}
                  />
                </td>
                <td>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(l.unitPriceCents / 100).toFixed(2)}
                    onChange={(e) =>
                      updateLine(idx, {
                        unitPriceCents: Math.round((Number(e.currentTarget.value) || 0) * 100),
                      })
                    }
                  />
                </td>
                <td>
                  <Select
                    value={l.incomeAccountId}
                    onChange={(e) => updateLine(idx, { incomeAccountId: e.currentTarget.value })}
                    required
                  >
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {a.name}
                      </option>
                    ))}
                  </Select>
                </td>
                <td className="kb-report__amt">
                  {fmt(Math.round(l.qty * l.unitPriceCents))}
                </td>
                <td>
                  {lines.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      ×
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            <tr className="kb-report__total">
              <td colSpan={4}>Subtotal</td>
              <td className="kb-report__amt">{fmt(subtotal)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() =>
            setLines((prev) => [
              ...prev,
              {
                description: '',
                qty: 1,
                unitPriceCents: 0,
                incomeAccountId: accounts[0]?.id ?? '',
              },
            ])
          }
        >
          + Add line
        </Button>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button
            type="submit"
            disabled={busy || !customerId || lines.some((l) => !l.incomeAccountId)}
          >
            {busy ? 'Saving…' : 'Save template'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
